"use client";

import { useContext, useEffect, useState } from "react";
import { useImmer } from "use-immer";
import { ClinicalCaseInProgress } from "./components/clinical-case-in-progress";
import { ClinicalCaseBeforeStart } from "./components/clinical-case-before-start";
import { ClinicalCaseSummary } from "./components/clinical-case-summary/clinical-case-summary";
import { clientPost } from "@/service/client-api/client-api";
import { ContentPatron } from "../../common/content-patron";
import { ResourceContext } from "@/contexts/resource-context";
import { logEvent } from "@/service/logs/log";
import { AssessmentScoreContext } from "@/contexts/assessment-score-context";
import { scrollToElement, scrollToPos } from "@/service/scroll/scroll";

function transformScoreToServerPayload(
  score: ClinicalCaseClientQuizScore,
): ClinicalCaseQuizScore {
  return {
    answers: Object.entries(score).map(([uuid, answer]) => ({
      uuid,
      correct: answer.correct,
    })),
  };
}

const STORAGE_PREFIX = "clinical-case-progress-";

export const ClinicalCase = ({
  clinicalCase,
  patron,
}: {
  clinicalCase: ClinicalCaseVM;
  patron: PatronVM | null;
}) => {
  const [status, setStatus] = useState<
    "before_start" | "in_progress" | "finished"
  >("before_start");
  const [score, setScore] = useImmer<ClinicalCaseClientQuizScore>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isRestored, setIsRestored] = useState(false);
  const { logView } = useContext(ResourceContext);

  const storageKey = `${STORAGE_PREFIX}${clinicalCase.id}`;

  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as {
          status: "in_progress";
          score: ClinicalCaseClientQuizScore;
          currentStep: number;
        };
        if (parsed.status === "in_progress") {
          setStatus("in_progress");
          setScore(parsed.score ?? {});
          setCurrentStep(parsed.currentStep ?? 0);
        }
      } catch {
        localStorage.removeItem(storageKey);
      }
    }
    setIsRestored(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isRestored) return;
    if (status === "in_progress") {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ status, score, currentStep }),
      );
    }
  }, [isRestored, status, score, currentStep, storageKey]);

  async function handleVisitFinish() {
    const newScore = transformScoreToServerPayload(score);
    await clientPost(
      "clinical_case.visit.finish",
      {
        id: clinicalCase.id,
      },
      newScore,
    );
    const scoreValues = Object.values(newScore.answers);
    logEvent("clinical_case_finish", logView, {
      correctAnswersCount: scoreValues.filter((answer) => answer.correct)
        .length,
      incorrectAnswersCount: scoreValues.filter((answer) => !answer.correct)
        .length,
      totalAnswersCount: Object.keys(newScore.answers).length,
    });
    setStatus("finished");
    localStorage.removeItem(storageKey);

    setTimeout(() => {
      scrollToElement({
        selector: "#clinical-case-summary",
        offset: -60,
        behavior: "smooth",
      });
    }, 100);
  }

  const startedOrFinished = ["in_progress", "finished"].includes(status);
  return (
    <AssessmentScoreContext.Provider value={{ score, setScore }}>
      <div>
        {status === "before_start" && (
          <ClinicalCaseBeforeStart
            clinicalCase={clinicalCase}
            onStartVisit={() => {
              logEvent("clinical_case_start", logView);
              setStatus("in_progress");
              scrollToPos({
                top: 0,
                left: 0,
                behavior: "smooth",
              });
            }}
          />
        )}

        {startedOrFinished && (
          <ClinicalCaseInProgress
            patron={patron}
            clinicalCase={clinicalCase}
            onFinish={handleVisitFinish}
            finished={status === "finished"}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        )}
        {status === "finished" && (
          <div className="my-8">
            <ClinicalCaseSummary clinicalCase={clinicalCase} score={score} />
          </div>
        )}
      </div>
      {status !== "finished" && patron && (
        <div className={"my-6"}>
          <ContentPatron patron={patron} type="clinical_case" />
        </div>
      )}
    </AssessmentScoreContext.Provider>
  );
};
